import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SITE_TZ, fmtDateTime } from '../lib/timezone';
import { TimezoneSelect } from '../components/booking/SlotPicker';
import { Card } from '../components/ui/card';
import { MentionTextarea } from '../components/MentionTextarea';
import { useUserNames } from '../context/UserNamesContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import ScheduleSection from '../components/schedule/ScheduleSection';
import ScheduleEventModal from '../components/schedule/ScheduleEventModal';
import SelfScheduleLinkModal from '../components/SelfScheduleLinkModal';
import DropZone from '../components/DropZone';
import { useFieldDraft } from '../hooks/useFieldDraft';
import {
  ArrowLeft,
  Save,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Briefcase,
  Clock,
  FileText,
  MessageSquare,
  Plus,
  Download,
  X,
  User,
  History,
  Upload,
  Send,
  UserCheck,
  UserPlus,
  Map,
  Circle,
  Star,
  Video,
  Building2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inactive statuses
const INACTIVE_STATUSES = [
  'Hired Elsewhere',
  'Not a Fit',
  'Withdrew Application',
  'No Show',
  'Archived',
];

export default function ApplicantDetail() {
  const { applicantId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [applicant, setApplicant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showSelfScheduleModal, setShowSelfScheduleModal] = useState(false);
  const [editedApplicant, setEditedApplicant] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [noteMentions, setNoteMentions] = useState([]);
  const { resolveName } = useUserNames();
  const [addingNote, setAddingNote] = useState(false);
  // Persist the in-progress applicant note across session timeouts / refreshes.
  const { clearDraft: clearApplicantNoteDraft } = useFieldDraft(
    `applicant-note:${applicantId}`,
    newNote,
    setNewNote
  );
  const [documentName, setDocumentName] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Hire modal state
  const [showHireModal, setShowHireModal] = useState(false);
  const [offerTemplates, setOfferTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [sendingOffer, setSendingOffer] = useState(false);

  // Convert to Personnel state
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [converting, setConverting] = useState(false);
  const [onboardingPacks, setOnboardingPacks] = useState([]);
  const [convertForm, setConvertForm] = useState({
    division: 'Therapy',
    discipline: '',
    personnel_type: 'Contractor',
    job_title: '',
    hire_date: new Date().toISOString().split('T')[0],
    onboarding_pack_id: '',
  });
  
  // Convert to Agency state
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [convertingAgency, setConvertingAgency] = useState(false);
  const [agencyName, setAgencyName] = useState('');

  // Coverage/Territory state
  const [coverage, setCoverage] = useState(null);
  const [coverageType, setCoverageType] = useState('radius');

  // Interviews state
  const [interviews, setInterviews] = useState([]);
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [interviewForm, setInterviewForm] = useState({ round: '1st', scheduled_date: '', scheduled_time: '', duration_minutes: 30, interviewer: '', interviewer_id: '', location: '', interview_type: 'In-Person', notes: '', timezone: SITE_TZ, add_meet: false });

  // Scorecards state
  const [scorecards, setScorecards] = useState([]);
  const [scorecardCriteria, setScorecardCriteria] = useState([
    { key: 'communication', label: 'Communication' },
    { key: 'experience', label: 'Experience' },
    { key: 'technical_skills', label: 'Technical Skills' },
    { key: 'culture_fit', label: 'Culture Fit' },
    { key: 'professionalism', label: 'Professionalism' },
  ]);
  const [showScorecardForm, setShowScorecardForm] = useState(false);
  const [scorecardForm, setScorecardForm] = useState({ interview_round: 'General', evaluator: '', ratings: { communication: 0, experience: 0, technical_skills: 0, culture_fit: 0, professionalism: 0 }, recommendation: '', notes: '' });
  const [zipCodes, setZipCodes] = useState('');
  const [cities, setCities] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(25);
  const [centerAddress, setCenterAddress] = useState('');
  const [savingCoverage, setSavingCoverage] = useState(false);

  useEffect(() => {
    fetchMetadata();
    fetchApplicant();
    fetchCoverage();
    fetchInterviews();
    fetchScorecards();
  }, [applicantId]);

  const fetchMetadata = async () => {
    try {
      const [statusRes, posTypes, empTypes, postingsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/ats/statuses`),
        axios.get(`${API}/ats/position-types`),
        axios.get(`${API}/ats/employment-types`),
        axios.get(`${API}/ats/job-postings`),
        axios.get(`${API}/esignature/templates`),
      ]);
      setStatuses(statusRes.data.statuses);
      setPositionTypes(posTypes.data.position_types);
      setEmploymentTypes(empTypes.data.employment_types);
      setJobPostings(postingsRes.data);
      // Prefer templates filed under an "Offer Letters" folder; fall back to all templates.
      const allTemplates = templatesRes.data || [];
      const offerFolder = allTemplates.filter(t => (t.folder || '').toLowerCase().includes('offer'));
      setOfferTemplates(offerFolder.length ? offerFolder : allTemplates);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const fetchApplicant = async () => {
    try {
      const response = await axios.get(`${API}/ats/applicants/${applicantId}`);
      setApplicant(response.data);
      setEditedApplicant({
        ...response.data,
        desired_coverage_areas: response.data.desired_coverage_areas?.join(', ') || '',
      });
      // Load scorecard criteria based on position
      fetchScorecardCriteria(response.data.position);
    } catch (error) {
      toast.error('Failed to load applicant');
      navigate('/personnel/applicants');
    } finally {
      setLoading(false);
    }
  };

  const fetchScorecardCriteria = async (position) => {
    if (!position) return;
    try {
      const res = await axios.get(`${API}/scorecard-criteria/by-position/${encodeURIComponent(position)}`);
      if (res.data?.criteria?.length) {
        setScorecardCriteria(res.data.criteria);
      }
    } catch (_) {
      // Keep default criteria if fetch fails
    }
  };

  const fetchCoverage = async () => {
    try {
      const response = await axios.get(`${API}/ats/applicants/${applicantId}/coverage`);
      if (response.data) {
        setCoverage(response.data);
        setCoverageType(response.data.coverage_type || 'radius');
        setZipCodes(response.data.zip_codes?.join(', ') || '');
        setCities(response.data.cities?.join(', ') || '');
        setRadiusMiles(response.data.radius_miles || 25);
        setCenterAddress(response.data.center_address || '');
      }
    } catch (error) {
      // No coverage set yet, that's fine
      console.log('No coverage data');
    }
  };

  const [interviewerOptions, setInterviewerOptions] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/interviewers`).then(r => setInterviewerOptions(r.data || [])).catch(() => {}); }, []);

  const fetchInterviews = async () => {
    try {
      const res = await axios.get(`${API}/ats/applicants/${applicantId}/interviews`);
      setInterviews(res.data || []);
    } catch { setInterviews([]); }
  };

  const fetchScorecards = async () => {
    try {
      const res = await axios.get(`${API}/ats/applicants/${applicantId}/scorecards`);
      setScorecards(res.data || []);
    } catch { setScorecards([]); }
  };

  const handleScheduleInterview = async () => {
    if (!interviewForm.scheduled_date) { toast.error('Please select a date'); return; }
    try {
      await axios.post(`${API}/ats/applicants/${applicantId}/interviews`, interviewForm);
      toast.success('Interview scheduled');
      setShowInterviewForm(false);
      setInterviewForm({ round: '1st', scheduled_date: '', scheduled_time: '', duration_minutes: 30, interviewer: '', interviewer_id: '', location: '', interview_type: 'In-Person', notes: '', timezone: SITE_TZ, add_meet: false });
      fetchInterviews();
    } catch (err) { toast.error('Failed to schedule interview'); }
  };

  const handleUpdateInterview = async (interviewId, updates) => {
    try {
      await axios.put(`${API}/ats/applicants/${applicantId}/interviews/${interviewId}`, updates);
      toast.success('Interview updated');
      fetchInterviews();
    } catch { toast.error('Update failed'); }
  };

  const handleSubmitScorecard = async () => {
    if (!scorecardForm.evaluator) { toast.error('Please enter evaluator name'); return; }
    try {
      await axios.post(`${API}/ats/applicants/${applicantId}/scorecards`, scorecardForm);
      toast.success('Scorecard submitted');
      setShowScorecardForm(false);
      const resetRatings = {};
      scorecardCriteria.forEach(c => { resetRatings[c.key] = 0; });
      setScorecardForm({ interview_round: 'General', evaluator: '', ratings: resetRatings, recommendation: '', notes: '' });
      fetchScorecards();
      fetchApplicant();
    } catch { toast.error('Failed to submit scorecard'); }
  };



  const handleSaveCoverage = async () => {
    setSavingCoverage(true);
    try {
      const payload = {
        coverage_type: coverageType,
        zip_codes: coverageType === 'zip_codes' 
          ? zipCodes.split(',').map(z => z.trim()).filter(z => z) 
          : [],
        cities: coverageType === 'cities' 
          ? cities.split(',').map(c => c.trim()).filter(c => c) 
          : [],
        radius_miles: coverageType === 'radius' ? radiusMiles : null,
        center_address: coverageType === 'radius' ? (centerAddress || applicant?.address) : null,
      };
      
      const response = await axios.post(`${API}/ats/applicants/${applicantId}/coverage`, payload);
      setCoverage(response.data.coverage);
      toast.success('Coverage area saved');
    } catch (error) {
      toast.error('Failed to save coverage area');
    } finally {
      setSavingCoverage(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editedApplicant,
        desired_coverage_areas: editedApplicant.desired_coverage_areas
          ? editedApplicant.desired_coverage_areas.split(',').map((a) => a.trim())
          : [],
      };
      await axios.put(`${API}/ats/applicants/${applicantId}`, payload);
      toast.success('Applicant updated successfully');
      setIsEditing(false);
      fetchApplicant();
    } catch (error) {
      toast.error('Failed to update applicant');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/ats/applicants/${applicantId}/status?status=${encodeURIComponent(newStatus)}`);
      toast.success('Status updated');
      fetchApplicant();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this applicant?')) return;
    try {
      await axios.delete(`${API}/ats/applicants/${applicantId}`);
      toast.success('Applicant deleted successfully');
      navigate('/personnel/applicants');
    } catch (error) {
      toast.error('Failed to delete applicant');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setAddingNote(true);
    try {
      await axios.post(
        `${API}/ats/applicants/${applicantId}/notes?content=${encodeURIComponent(newNote)}&mentions=${encodeURIComponent(JSON.stringify(noteMentions))}`
      );
      toast.success('Note added');
      setNewNote('');
      setNoteMentions([]);
      clearApplicantNoteDraft();
      fetchApplicant();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/ats/applicants/${applicantId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchApplicant();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleFileUpload = async (fileOrEvent) => {
    const file = fileOrEvent?.target?.files
      ? fileOrEvent.target.files[0]
      : fileOrEvent;
    if (!file) return;
    if (!documentName.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    setUploadingDoc(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${API}/ats/applicants/${applicantId}/documents?document_name=${encodeURIComponent(documentName)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Document uploaded');
      setDocumentName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchApplicant();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (docId, fileName) => {
    try {
      const response = await axios.get(
        `${API}/ats/applicants/${applicantId}/documents/${docId}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/ats/applicants/${applicantId}/documents/${docId}`);
      toast.success('Document deleted');
      fetchApplicant();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const openHireModal = () => {
    // Pre-populate some placeholder values from applicant data
    const defaultValues = {
      applicant_name: `${applicant.first_name} ${applicant.last_name}`,
      first_name: applicant.first_name || '',
      last_name: applicant.last_name || '',
      email: applicant.email || '',
      position: applicant.desired_position || '',
      employment_type: applicant.employment_type || '',
    };
    setPlaceholderValues(defaultValues);
    setSelectedTemplate(null);
    setShowHireModal(true);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    if (!template) { setPlaceholderValues({}); return; }
    // E-Signature templates expose positioned `fields`. Pre-fill the text/date/name/email
    // ones with the template default or matched applicant data so the sender can edit before sending.
    const vals = {};
    (template.fields || [])
      .filter((f) => ['text', 'date', 'name', 'email'].includes(f.type))
      .forEach((f) => {
        let v = f.default_value || '';
        const label = (f.label || '').toLowerCase();
        if (!v) {
          if (f.type === 'email' || label.includes('email')) v = applicant.email || '';
          else if (f.type === 'name' || label.includes('name')) v = `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim();
          else if (label.includes('position') || label.includes('title')) v = applicant.desired_position || '';
        }
        vals[f.id] = v;
      });
    setPlaceholderValues(vals);
  };

  const handleSendOffer = async () => {
    if (!selectedTemplate) {
      toast.error('Please select an offer letter template');
      return;
    }
    if (!applicant.email) {
      toast.error('Applicant does not have an email address');
      return;
    }

    setSendingOffer(true);
    try {
      const fieldValues = Object.entries(placeholderValues)
        .filter(([, v]) => v !== '' && v != null)
        .map(([field_id, value]) => ({ field_id, value }));

      // Seats the applicant as the Contractor (the role that owns the signature
      // field), auto-completes the company Rep with the values entered here, and
      // emails only the contractor. See backend /requests/send-offer.
      const res = await axios.post(`${API}/esignature/requests/send-offer`, {
        template_id: selectedTemplate.id,
        applicant_id: applicantId,
        applicant_name: `${applicant.first_name} ${applicant.last_name}`.trim(),
        applicant_email: applicant.email,
        field_values: fieldValues,
        expires_days: Number(selectedTemplate.expiration_days) || 5,
        from_choice: 'admin',
      });
      if (!res.data?.request_id) throw new Error('Failed to create offer');

      // Update applicant status to "Offer Extended"
      await axios.put(`${API}/ats/applicants/${applicantId}`, {
        ...applicant,
        status: 'Offer Extended',
      });

      toast.success(res.data?.email_sent === false
        ? 'Offer created (email not sent — check mailbox settings)'
        : 'Offer letter sent successfully!');
      setShowHireModal(false);
      fetchApplicant();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send offer letter');
    } finally {
      setSendingOffer(false);
    }
  };


  // ---- Convert to Personnel ---- //
  const openConvertModal = async () => {
    const od = applicant.offer_details || {};
    setConvertForm(prev => ({
      ...prev,
      // Pull as much as possible from the applicant profile + the offer letter;
      // fall back to blanks so the user can fill anything missing.
      discipline: od.discipline || applicant.discipline || applicant.desired_position || prev.discipline || '',
      job_title: od.job_title || applicant.desired_position || prev.job_title || '',
      hire_date: od.hire_date || prev.hire_date || '',
      pay_rate: od.pay_rate || applicant.desired_pay || prev.pay_rate || '',
      personnel_type: applicant.applicant_type || applicant.personnel_type || prev.personnel_type || '',
      division: applicant.division || prev.division || '',
      location: od.location || applicant.city || prev.location || '',
    }));
    // Fetch onboarding packs (presets)
    try {
      const res = await axios.get(`${API}/onboarding/packs`);
      setOnboardingPacks(res.data || []);
    } catch { setOnboardingPacks([]); }
    setShowConvertModal(true);
  };

  const handleConvert = async () => {
    setConverting(true);
    try {
      const res = await axios.post(`${API}/ats/applicants/${applicantId}/convert-to-personnel`, {
        ...convertForm,
        converted_by: 'Admin',
      });
      toast.success(res.data.message);
      if (res.data.signatures_dispatched > 0) {
        toast.success(`${res.data.signatures_dispatched} signature document(s) sent for signing`);
      }
      if (res.data.portal_activation_link && !res.data.portal_invite_emailed) {
        toast.info('Email not connected — copy the portal activation link from the new hire\'s record');
      } else if (res.data.portal_invite_emailed) {
        toast.success('Portal activation link emailed to the new hire');
      }
      setShowConvertModal(false);
      // Navigate to new personnel record
      navigate(`/personnel/${res.data.personnel_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };


  const openAgencyModal = () => {
    setAgencyName(`${applicant.first_name || ''} ${applicant.last_name || ''}`.trim());
    setShowAgencyModal(true);
  };

  const handleConvertToAgency = async () => {
    if (!agencyName.trim()) {
      toast.error('Agency name is required');
      return;
    }
    setConvertingAgency(true);
    try {
      const res = await axios.post(`${API}/ats/applicants/${applicantId}/convert-to-agency`, {
        agency_name: agencyName.trim(),
      });
      toast.success(res.data.message);
      setShowAgencyModal(false);
      navigate(`/agencies/${res.data.agency_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertingAgency(false);
    }
  };


  const getStatusColor = (status) => {
    if (INACTIVE_STATUSES.includes(status)) {
      return 'bg-gray-100 text-gray-600';
    }
    switch (status) {
      case 'New':
        return 'bg-blue-100 text-blue-800';
      case 'Waiting to Schedule 1st Interview':
      case 'Waiting to Schedule 2nd Interview':
        return 'bg-yellow-100 text-yellow-800';
      case '1st Interview Scheduled':
      case '2nd Interview Scheduled':
        return 'bg-purple-100 text-purple-800';
      case '1st Interview Complete':
      case '2nd Interview Complete':
        return 'bg-indigo-100 text-indigo-800';
      case 'Background Check':
        return 'bg-orange-100 text-orange-800';
      case 'Offer Extended':
        return 'bg-pink-100 text-pink-800';
      case 'Offer Accepted':
      case 'Hired':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Loading applicant...
      </div>
    );
  }

  if (!applicant) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Applicant not found</p>
        <Button className="mt-4" onClick={() => navigate('/personnel/applicants')}>
          Back to Applicants
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/personnel/applicants')}
            data-testid="back-btn"
          >
            <ArrowLeft size={18} className="mr-1" />
            Back
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-semibold text-foreground tracking-tight">
              {applicant.first_name} {applicant.last_name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <Badge className={getStatusColor(applicant.status)}>
                {applicant.status}
              </Badge>
              {applicant.job_posting && (
                <span
                  className="text-sm text-primary cursor-pointer hover:underline"
                  onClick={() =>
                    navigate(`/ats/postings/${applicant.job_posting.id}`)
                  }
                >
                  Applied for: {applicant.job_posting.title}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditing(false);
                  setEditedApplicant({
                    ...applicant,
                    desired_coverage_areas:
                      applicant.desired_coverage_areas?.join(', ') || '',
                  });
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              {applicant.offer_request_id ? (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => navigate(`/admin/signatures?open=${applicant.offer_request_id}`)}
                  data-testid="offer-sent-btn"
                  title="View / edit / cancel the sent offer document"
                >
                  <UserCheck size={16} className="mr-2" />
                  Offer Sent!
                </Button>
              ) : (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={openHireModal}
                  data-testid="hire-btn"
                >
                  <UserCheck size={16} className="mr-2" />
                  Send Offer
                </Button>
              )}
              {!applicant.personnel_id && (
                <Button
                  className="bg-primary hover:bg-primary/90"
                  onClick={openConvertModal}
                  data-testid="convert-to-personnel-btn"
                >
                  <UserPlus size={16} className="mr-2" />
                  Hire
                </Button>
              )}
              {applicant.personnel_id && (
                <Button variant="outline" onClick={() => navigate(`/personnel/${applicant.personnel_id}`)} data-testid="view-personnel-btn">
                  <User size={16} className="mr-2" />
                  View Personnel
                </Button>
              )}
              <Button
                variant="outline"
                onClick={openAgencyModal}
                data-testid="convert-to-agency-btn"
              >
                <Building2 size={16} className="mr-2" />
                Convert to Prospective Agency
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                data-testid="edit-btn"
              >
                Edit
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowScheduleModal(true)}
                data-testid="schedule-top-btn"
                className="gap-2"
              >
                <Calendar size={16} />
                Schedule
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                data-testid="delete-btn"
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-8">
              <TabsTrigger value="details" data-testid="tab-details">
                <User size={14} className="mr-1" />
                Details
              </TabsTrigger>
              <TabsTrigger value="coverage" data-testid="tab-coverage">
                <Map size={14} className="mr-1" />
                Coverage
              </TabsTrigger>
              <TabsTrigger value="notes" data-testid="tab-notes">
                <MessageSquare size={14} className="mr-1" />
                Notes ({applicant.notes?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents">
                <FileText size={14} className="mr-1" />
                Documents ({(applicant.documents?.length || 0) + (applicant.resume_url ? 1 : 0)})
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <History size={14} className="mr-1" />
                History
              </TabsTrigger>
              <TabsTrigger value="interviews" data-testid="tab-interviews">
                <Video size={14} className="mr-1" />
                Interviews ({interviews.length})
              </TabsTrigger>
              <TabsTrigger value="scorecards" data-testid="tab-scorecards">
                <Star size={14} className="mr-1" />
                Scorecards ({scorecards.length})
              </TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule">
                <Calendar size={14} className="mr-1" />
                Schedule
              </TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details">
              <Card className="p-6">
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          First Name
                        </label>
                        <Input
                          value={editedApplicant.first_name}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              first_name: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Last Name
                        </label>
                        <Input
                          value={editedApplicant.last_name}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              last_name: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Email
                        </label>
                        <Input
                          type="email"
                          value={editedApplicant.email || ''}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              email: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Phone
                        </label>
                        <Input
                          value={editedApplicant.phone || ''}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              phone: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Address
                      </label>
                      <Input
                        value={editedApplicant.address || ''}
                        onChange={(e) =>
                          setEditedApplicant({
                            ...editedApplicant,
                            address: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          City
                        </label>
                        <Input
                          value={editedApplicant.city || ''}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              city: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          State
                        </label>
                        <Input
                          value={editedApplicant.state || ''}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              state: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Zip Code
                        </label>
                        <Input
                          value={editedApplicant.zip_code || ''}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              zip_code: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <h3 className="font-medium mb-3">Position Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">
                            Job Posting
                          </label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={editedApplicant.job_posting_id || ''}
                            onChange={(e) =>
                              setEditedApplicant({
                                ...editedApplicant,
                                job_posting_id: e.target.value,
                              })
                            }
                          >
                            <option value="">No specific posting</option>
                            {jobPostings.map((posting) => (
                              <option key={posting.id} value={posting.id}>
                                {posting.title}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">
                            Desired Position
                          </label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={editedApplicant.desired_position || ''}
                            onChange={(e) =>
                              setEditedApplicant({
                                ...editedApplicant,
                                desired_position: e.target.value,
                              })
                            }
                          >
                            <option value="">Select...</option>
                            {positionTypes.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">
                            Employment Preference
                          </label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={editedApplicant.employment_type_preference}
                            onChange={(e) =>
                              setEditedApplicant({
                                ...editedApplicant,
                                employment_type_preference: e.target.value,
                              })
                            }
                          >
                            {employmentTypes.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">
                            Available Start Date
                          </label>
                          <Input
                            type="date"
                            value={editedApplicant.available_start_date || ''}
                            onChange={(e) =>
                              setEditedApplicant({
                                ...editedApplicant,
                                available_start_date: e.target.value,
                              })
                            }
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <label className="text-sm font-medium text-muted-foreground">
                          Desired Coverage Areas (comma-separated)
                        </label>
                        <Input
                          value={editedApplicant.desired_coverage_areas || ''}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              desired_coverage_areas: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="mt-4">
                        <label className="text-sm font-medium text-muted-foreground">
                          Source
                        </label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={editedApplicant.source || ''}
                          onChange={(e) =>
                            setEditedApplicant({
                              ...editedApplicant,
                              source: e.target.value,
                            })
                          }
                        >
                          <option value="">Select source...</option>
                          <option value="Indeed">Indeed</option>
                          <option value="Glassdoor">Glassdoor</option>
                          <option value="LinkedIn">LinkedIn</option>
                          <option value="Referral">Referral</option>
                          <option value="Website">Website</option>
                          <option value="Walk-in">Walk-in</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Contact Info */}
                    <div>
                      <h3 className="font-medium mb-3">Contact Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {applicant.email && (
                          <div className="flex items-center gap-2">
                            <Mail size={16} className="text-muted-foreground" />
                            <a
                              href={`mailto:${applicant.email}`}
                              className="text-sm text-primary hover:underline"
                            >
                              {applicant.email}
                            </a>
                          </div>
                        )}
                        {applicant.phone && (
                          <div className="flex items-center gap-2">
                            <Phone size={16} className="text-muted-foreground" />
                            <a
                              href={`tel:${applicant.phone}`}
                              className="text-sm text-primary hover:underline"
                            >
                              {applicant.phone}
                            </a>
                          </div>
                        )}
                      </div>
                      {(applicant.address ||
                        applicant.city ||
                        applicant.state ||
                        applicant.zip_code) && (
                        <div className="flex items-start gap-2 mt-2">
                          <MapPin size={16} className="text-muted-foreground mt-0.5" />
                          <span className="text-sm">
                            {[
                              applicant.address,
                              applicant.city,
                              applicant.state,
                              applicant.zip_code,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Position Info */}
                    <div className="border-t pt-4">
                      <h3 className="font-medium mb-3">Position Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {applicant.desired_position && (
                          <div className="flex items-center gap-2">
                            <Briefcase size={16} className="text-muted-foreground" />
                            <span className="text-sm">
                              Desired: {applicant.desired_position}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="text-muted-foreground" />
                          <span className="text-sm">
                            {applicant.employment_type_preference}
                          </span>
                        </div>
                        {applicant.available_start_date && (
                          <div className="flex items-center gap-2">
                            <Calendar size={16} className="text-muted-foreground" />
                            <span className="text-sm">
                              Available: {formatDate(applicant.available_start_date)}
                            </span>
                          </div>
                        )}
                        {applicant.source && (
                          <div className="text-sm text-muted-foreground">
                            Source: {applicant.source}
                          </div>
                        )}
                      </div>
                      {applicant.desired_coverage_areas &&
                        applicant.desired_coverage_areas.length > 0 && (
                          <div className="mt-3">
                            <span className="text-sm text-muted-foreground">
                              Coverage Areas:
                            </span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {applicant.desired_coverage_areas.map((area, idx) => (
                                <Badge key={idx} variant="outline">
                                  {area}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Coverage Tab */}
            <TabsContent value="coverage">
              <Card className="p-6">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <MapPin size={18} />
                  Coverage Area / Territory
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Define the areas this applicant would cover for home visits. This will be displayed on the map overlay.
                </p>
                
                <div className="space-y-4">
                  {/* Coverage Type Selection */}
                  <div>
                    <label className="text-sm font-medium">Coverage Type</label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="coverageType"
                          value="radius"
                          checked={coverageType === 'radius'}
                          onChange={(e) => setCoverageType(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Radius from Address</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="coverageType"
                          value="zip_codes"
                          checked={coverageType === 'zip_codes'}
                          onChange={(e) => setCoverageType(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">ZIP Codes</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="coverageType"
                          value="cities"
                          checked={coverageType === 'cities'}
                          onChange={(e) => setCoverageType(e.target.value)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Cities</span>
                      </label>
                    </div>
                  </div>

                  {/* Radius Input */}
                  {coverageType === 'radius' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Center Address</label>
                        <Input
                          value={centerAddress}
                          onChange={(e) => setCenterAddress(e.target.value)}
                          placeholder={applicant?.address || "Enter address or leave blank to use applicant's address"}
                          data-testid="center-address-input"
                        />
                        {applicant?.address && !centerAddress && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Will use: {applicant.address}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-sm font-medium">Radius (miles)</label>
                        <Input
                          type="number"
                          value={radiusMiles}
                          onChange={(e) => setRadiusMiles(parseInt(e.target.value) || 25)}
                          min={1}
                          max={100}
                          data-testid="radius-input"
                        />
                      </div>
                    </div>
                  )}

                  {/* ZIP Codes Input */}
                  {coverageType === 'zip_codes' && (
                    <div>
                      <label className="text-sm font-medium">ZIP Codes (comma separated)</label>
                      <Input
                        value={zipCodes}
                        onChange={(e) => setZipCodes(e.target.value)}
                        placeholder="77001, 77002, 77003, 77004..."
                        data-testid="zip-codes-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter multiple ZIP codes separated by commas. A polygon will be created connecting these areas.
                      </p>
                    </div>
                  )}

                  {/* Cities Input */}
                  {coverageType === 'cities' && (
                    <div>
                      <label className="text-sm font-medium">Cities (comma separated)</label>
                      <Input
                        value={cities}
                        onChange={(e) => setCities(e.target.value)}
                        placeholder="Houston, Katy, Sugar Land, Pearland..."
                        data-testid="cities-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter city names separated by commas. A polygon will be created connecting these cities.
                      </p>
                    </div>
                  )}

                  <Button 
                    onClick={handleSaveCoverage} 
                    disabled={savingCoverage}
                    data-testid="save-coverage-btn"
                  >
                    {savingCoverage ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Saving & Geocoding...
                      </>
                    ) : (
                      <>
                        <Save size={14} className="mr-2" />
                        Save Coverage Area
                      </>
                    )}
                  </Button>

                  {/* Current Coverage Display */}
                  {coverage && (
                    <div className="mt-4 p-4 bg-muted rounded-lg">
                      <h4 className="font-medium text-sm mb-2">Current Coverage</h4>
                      <div className="text-sm space-y-1">
                        <p><span className="text-muted-foreground">Type:</span> {coverage.coverage_type}</p>
                        {coverage.coverage_type === 'radius' && (
                          <>
                            <p><span className="text-muted-foreground">Center:</span> {coverage.center_address}</p>
                            <p><span className="text-muted-foreground">Radius:</span> {coverage.radius_miles} miles</p>
                          </>
                        )}
                        {coverage.coverage_type === 'zip_codes' && (
                          <p><span className="text-muted-foreground">ZIP Codes:</span> {coverage.zip_codes?.join(', ')}</p>
                        )}
                        {coverage.coverage_type === 'cities' && (
                          <p><span className="text-muted-foreground">Cities:</span> {coverage.cities?.join(', ')}</p>
                        )}
                        {coverage.polygon_coordinates?.length > 0 && (
                          <p className="text-green-600 flex items-center gap-1">
                            <Circle size={12} fill="currentColor" />
                            Polygon created with {coverage.polygon_coordinates.length} points
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes">
              <Card className="p-6">
                <div className="space-y-4">
                  {/* Add Note */}
                  <div className="flex gap-2">
                    <MentionTextarea
                      value={newNote}
                      onChange={setNewNote}
                      mentions={noteMentions}
                      onMentionsChange={setNoteMentions}
                      placeholder="Add interview notes… type @ to tag a teammate"
                      rows={3}
                      testId="applicant-note"
                      onSubmitShortcut={handleAddNote}
                    />
                  </div>
                  <Button
                    onClick={handleAddNote}
                    disabled={addingNote}
                    data-testid="add-note-btn"
                  >
                    <Plus size={16} className="mr-2" />
                    {addingNote ? 'Adding...' : 'Add Note'}
                  </Button>

                  {/* Notes List */}
                  <div className="border-t pt-4 space-y-4">
                    {applicant.notes && applicant.notes.length > 0 ? (
                      applicant.notes
                        .sort(
                          (a, b) =>
                            new Date(b.created_at) - new Date(a.created_at)
                        )
                        .map((note) => (
                          <div
                            key={note.id}
                            className="p-4 bg-muted/30 rounded-lg"
                            data-testid={`note-${note.id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-sm whitespace-pre-wrap">
                                  {note.content}
                                </p>
                                {note.mentions?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {note.mentions.map((m) => (
                                      <span key={m.user_id} className="inline-flex items-center gap-0.5 text-[11px] font-medium text-blue-700 bg-blue-50 rounded-full px-1.5 py-0.5">@{m.name}</span>
                                    ))}
                                  </div>
                                )}
                                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                                  <span>{resolveName(note.author)}</span>
                                  <span>•</span>
                                  <span>{formatDateTime(note.created_at)}</span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => handleDeleteNote(note.id)}
                                data-testid={`delete-note-${note.id}`}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </div>
                          </div>
                        ))
                    ) : (
                      <p className="text-center text-muted-foreground py-4">
                        No notes yet
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <Card className="p-6">
                <div className="space-y-4">
                  {/* Resume submitted with the application */}
                  {applicant.resume_url && (
                    <div className="flex items-center justify-between p-4 rounded-lg border border-primary/30 bg-primary/5" data-testid="application-resume">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText size={22} className="text-primary flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium">Resume / CV</p>
                          <p className="text-xs text-muted-foreground">Submitted with the application</p>
                        </div>
                      </div>
                      <a
                        href={`${BACKEND_URL}${applicant.resume_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:opacity-90"
                        data-testid="download-application-resume"
                      >
                        <Download size={14} /> View / Download
                      </a>
                    </div>
                  )}

                  {/* Upload Document */}
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Document Name
                      </label>
                      <Input
                        value={documentName}
                        onChange={(e) => setDocumentName(e.target.value)}
                        placeholder="e.g., Resume, License, Certification"
                        data-testid="document-name-input"
                      />
                    </div>
                    <DropZone
                      onFile={(f) => handleFileUpload(f)}
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.bmp,.webp,.txt"
                      label={uploadingDoc ? 'Uploading…' : 'Drag and drop a document'}
                      hint="PDF, image, or DOC"
                      disabled={uploadingDoc || !documentName.trim()}
                      testId="applicant-doc-upload"
                      compact
                    />
                    {!documentName.trim() && (
                      <p className="text-xs text-muted-foreground">
                        Enter a document name to enable upload.
                      </p>
                    )}
                  </div>

                  {/* Documents List */}
                  <div className="border-t pt-4 space-y-2">
                    {applicant.documents && applicant.documents.length > 0 ? (
                      applicant.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                          data-testid={`document-${doc.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <FileText size={20} className="text-primary" />
                            <div>
                              <p className="font-medium text-sm">
                                {doc.document_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {doc.file_name} •{' '}
                                {(doc.file_size / 1024).toFixed(1)} KB •{' '}
                                {formatDate(doc.uploaded_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleDownloadDocument(doc.id, doc.file_name)
                              }
                              data-testid={`download-doc-${doc.id}`}
                            >
                              <Download size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDeleteDocument(doc.id)}
                              data-testid={`delete-doc-${doc.id}`}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-4">
                        No documents uploaded
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history">
              <Card className="p-6">
                <h3 className="font-medium mb-4">Status History</h3>
                {applicant.status_history && applicant.status_history.length > 0 ? (
                  <div className="space-y-3">
                    {applicant.status_history
                      .sort(
                        (a, b) =>
                          new Date(b.changed_at) - new Date(a.changed_at)
                      )
                      .map((entry, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg"
                        >
                          <Badge className={getStatusColor(entry.status)}>
                            {entry.status}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {formatDateTime(entry.changed_at)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            by {entry.changed_by}
                          </span>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">
                    No status history
                  </p>
                )}
              </Card>
            </TabsContent>

            {/* Interviews Tab */}
            <TabsContent value="interviews">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-lg">Interviews</h3>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowSelfScheduleModal(true)}
                      className="gap-1"
                      data-testid="send-self-schedule-btn"
                    >
                      <Send size={14} /> Send Self-Schedule Link
                    </Button>
                    <Button size="sm" onClick={() => setShowInterviewForm(true)} className="gap-1" data-testid="schedule-interview-btn">
                      <Plus size={14} /> Schedule Interview
                    </Button>
                  </div>
                </div>

                {showInterviewForm && (
                  <div className="p-4 border rounded-lg mb-4 bg-muted/30 space-y-3" data-testid="interview-form">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-medium">Round</label>
                        <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={interviewForm.round}
                          onChange={e => setInterviewForm(p => ({ ...p, round: e.target.value }))}>
                          <option>1st</option><option>2nd</option><option>Final</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Date *</label>
                        <Input type="date" className="h-9 mt-1" value={interviewForm.scheduled_date}
                          onChange={e => setInterviewForm(p => ({ ...p, scheduled_date: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Time</label>
                        <Input type="time" className="h-9 mt-1" value={interviewForm.scheduled_time}
                          onChange={e => setInterviewForm(p => ({ ...p, scheduled_time: e.target.value }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-medium">Type</label>
                        <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={interviewForm.interview_type}
                          onChange={e => setInterviewForm(p => ({ ...p, interview_type: e.target.value }))}>
                          <option>In-Person</option><option>Phone</option><option>Video</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Interviewer</label>
                        <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={interviewForm.interviewer_id}
                          data-testid="schedule-interviewer-select"
                          onChange={e => {
                            const iv = interviewerOptions.find(x => x.id === e.target.value);
                            setInterviewForm(p => ({ ...p, interviewer_id: e.target.value, interviewer: iv ? `${iv.first_name} ${iv.last_name}` : '',
                              timezone: iv?.interviewer_timezone || p.timezone, add_meet: iv ? (iv.booking_settings?.meet_default ?? true) : p.add_meet }));
                          }}>
                          <option value="">Select…</option>
                          {interviewerOptions.map(x => <option key={x.id} value={x.id}>{x.first_name} {x.last_name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-xs font-medium">Location/Link</label>
                        <Input className="h-9 mt-1" placeholder="Office or URL" value={interviewForm.location}
                          onChange={e => setInterviewForm(p => ({ ...p, location: e.target.value }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 items-end">
                      <div className="col-span-2">
                        <label className="text-xs font-medium">Timezone (date & time above are in this zone)</label>
                        <TimezoneSelect value={interviewForm.timezone} onChange={v => setInterviewForm(p => ({ ...p, timezone: v }))} testId="schedule-timezone-select" className="mt-1" />
                      </div>
                      <label className="flex items-center gap-2 text-sm h-9">
                        <input type="checkbox" checked={interviewForm.add_meet} onChange={e => setInterviewForm(p => ({ ...p, add_meet: e.target.checked }))} data-testid="schedule-add-meet" />
                        Add Google Meet link
                      </label>
                    </div>
                    {(() => {
                      const sel = interviewerOptions.find(x => x.id === interviewForm.interviewer_id);
                      if (sel && !sel.google_calendar?.connected) {
                        return <p className="text-xs text-amber-700" data-testid="schedule-gcal-hint">{sel.first_name}'s Google Calendar isn't connected, so this won't sync and no Meet link will be created. Connect it under Careers → Interviewers.</p>;
                      }
                      return <p className="text-xs text-muted-foreground">Syncs to the interviewer's Google Calendar when it's connected.</p>;
                    })()}
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setShowInterviewForm(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleScheduleInterview} data-testid="confirm-schedule-btn">Schedule</Button>
                    </div>
                  </div>
                )}

                {interviews.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No interviews scheduled</p>
                ) : (
                  <div className="space-y-3">
                    {interviews.map((iv, i) => (
                      <div key={iv.id} className="flex items-center gap-4 p-3 border rounded-lg" data-testid={`interview-${i}`}>
                        <div className="flex-shrink-0">
                          <Badge className={iv.status === 'Completed' ? 'bg-green-100 text-green-700' : iv.status === 'Cancelled' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>
                            {iv.round} — {iv.status}
                          </Badge>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium" data-testid={`interview-when-${i}`}>{iv.starts_at ? fmtDateTime(iv.starts_at, iv.timezone || SITE_TZ) : `${iv.scheduled_date || ''} ${iv.scheduled_time ? `at ${iv.scheduled_time}` : ''}`}</p>
                          <p className="text-xs text-muted-foreground">{iv.interview_type} {iv.interviewer && `with ${iv.interviewer}`} {iv.location && `| ${iv.location}`}</p>
                          {iv.meeting_link && iv.status !== 'Cancelled' && <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="text-xs text-primary underline" data-testid={`interview-meet-${i}`}>{iv.meeting_link}</a>}
                          {iv.gcal_event_id && <span className="ml-2 text-[11px] text-emerald-700">On Google Calendar</span>}
                        </div>
                        {iv.status === 'Scheduled' && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleUpdateInterview(iv.id, { status: 'Completed' })}>Complete</Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => handleUpdateInterview(iv.id, { status: 'Cancelled' })}>Cancel</Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Scorecards Tab */}
            <TabsContent value="scorecards">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">Scorecards</h3>
                    {applicant.avg_score > 0 && <p className="text-sm text-muted-foreground">Average Score: <span className="font-bold text-primary">{applicant.avg_score}/5</span></p>}
                  </div>
                  <Button size="sm" onClick={() => setShowScorecardForm(true)} className="gap-1" data-testid="add-scorecard-btn">
                    <Plus size={14} /> Add Scorecard
                  </Button>
                </div>

                {showScorecardForm && (
                  <div className="p-4 border rounded-lg mb-4 bg-muted/30 space-y-3" data-testid="scorecard-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium">Evaluator *</label>
                        <Input className="h-9 mt-1" placeholder="Your name" value={scorecardForm.evaluator}
                          onChange={e => setScorecardForm(p => ({ ...p, evaluator: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs font-medium">Interview Round</label>
                        <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={scorecardForm.interview_round}
                          onChange={e => setScorecardForm(p => ({ ...p, interview_round: e.target.value }))}>
                          <option>General</option><option>1st Interview</option><option>2nd Interview</option><option>Final</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-2 block">Ratings (1-5)</label>
                      <div className="grid grid-cols-1 gap-2">
                        {scorecardCriteria.map(crit => {
                          const val = scorecardForm.ratings[crit.key] || 0;
                          return (
                            <div key={crit.key} className="flex items-center gap-3">
                              <span className="text-sm w-36" title={crit.description || ''}>{crit.label}</span>
                              <div className="flex gap-1">
                                {[1,2,3,4,5].map(n => (
                                  <button key={n} onClick={() => setScorecardForm(p => ({ ...p, ratings: { ...p.ratings, [crit.key]: n } }))}
                                    className={`w-8 h-8 rounded-full border text-xs font-bold transition-colors ${val >= n ? 'bg-primary text-white border-primary' : 'bg-background hover:bg-muted'}`}
                                    data-testid={`rating-${crit.key}-${n}`}>
                                    {n}
                                  </button>
                                ))}
                              </div>
                              <span className="text-xs text-muted-foreground w-8">{val}/5</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium">Recommendation</label>
                      <select className="flex h-9 w-full rounded-md border px-2 py-1 text-sm mt-1" value={scorecardForm.recommendation}
                        onChange={e => setScorecardForm(p => ({ ...p, recommendation: e.target.value }))}>
                        <option value="">Select...</option>
                        <option>Strong Hire</option><option>Hire</option><option>Maybe</option><option>No Hire</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium">Notes</label>
                      <textarea className="flex w-full rounded-md border px-3 py-2 text-sm mt-1 min-h-[60px]" value={scorecardForm.notes}
                        onChange={e => setScorecardForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes..." />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setShowScorecardForm(false)}>Cancel</Button>
                      <Button size="sm" onClick={handleSubmitScorecard} data-testid="submit-scorecard-btn">Submit Scorecard</Button>
                    </div>
                  </div>
                )}

                {scorecards.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No scorecards yet</p>
                ) : (
                  <div className="space-y-3">
                    {scorecards.map((sc, i) => (
                      <div key={sc.id} className="p-4 border rounded-lg" data-testid={`scorecard-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{sc.evaluator}</span>
                            <Badge className="text-[10px] bg-muted">{sc.interview_round}</Badge>
                            {sc.recommendation && (
                              <Badge className={
                                sc.recommendation === 'Strong Hire' ? 'bg-green-100 text-green-700' :
                                sc.recommendation === 'Hire' ? 'bg-blue-100 text-blue-700' :
                                sc.recommendation === 'Maybe' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }>{sc.recommendation}</Badge>
                            )}
                          </div>
                          <span className="text-lg font-bold text-primary">{sc.overall_score}/5</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs">
                          {Object.entries(sc.ratings || {}).map(([key, val]) => (
                            <span key={key} className="flex items-center gap-1">
                              <span className="capitalize text-muted-foreground">{key.replace(/_/g, ' ')}:</span>
                              <span className="font-medium">{val}</span>
                            </span>
                          ))}
                        </div>
                        {sc.notes && <p className="text-xs text-muted-foreground mt-2 italic">{sc.notes}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1">{new Date(sc.created_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Schedule Tab */}
            <TabsContent value="schedule">
              <ScheduleSection
                relatedType="applicant"
                relatedId={applicantId}
                defaultAttendee={applicant ? {
                  kind: 'applicant',
                  ref_id: applicant.id,
                  name: `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim(),
                  email: applicant.email || null,
                } : null}
                title={`Schedule for ${applicant?.first_name || ''}`.trim()}
              />
            </TabsContent>

          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Status Update */}
          <Card className="p-6">
            <h2 className="font-semibold text-lg mb-4">Update Status</h2>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mb-3"
              value={applicant.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              data-testid="status-select"
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
            <div className="text-xs text-muted-foreground">
              {INACTIVE_STATUSES.includes(applicant.status) && (
                <span className="text-orange-600">
                  This is an inactive status
                </span>
              )}
            </div>
          </Card>

          {/* Quick Info */}
          <Card className="p-6">
            <h2 className="font-semibold text-lg mb-4">Quick Info</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Applied</span>
                <span>{formatDate(applicant.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Last Updated</span>
                <span>{formatDate(applicant.updated_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Notes</span>
                <span>{applicant.notes?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Documents</span>
                <span>{applicant.documents?.length || 0}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>


      {/* Convert to Prospective Agency Modal */}
      {showAgencyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4" data-testid="convert-agency-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Convert to Prospective Agency</h2>
                <p className="text-sm text-muted-foreground">{applicant.first_name} {applicant.last_name}</p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowAgencyModal(false)} data-testid="close-agency-modal-btn">
                <X size={18} />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Agency Name</label>
                <Input
                  value={agencyName}
                  onChange={(e) => setAgencyName(e.target.value)}
                  placeholder="Agency name"
                  data-testid="agency-name-input"
                />
              </div>
              <p className="text-sm text-muted-foreground">
                This will create a Prospective Agency using the applicant's contact
                details and remove this applicant from the ATS pipeline.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAgencyModal(false)} data-testid="cancel-agency-btn">
                  Cancel
                </Button>
                <Button onClick={handleConvertToAgency} disabled={convertingAgency} data-testid="confirm-agency-btn">
                  <Building2 size={16} className="mr-2" />
                  {convertingAgency ? 'Converting...' : 'Create Agency'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}


      {/* Convert to Personnel Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg p-6 m-4 max-h-[90vh] overflow-y-auto" data-testid="convert-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Convert to Personnel</h2>
                <p className="text-sm text-muted-foreground">{applicant.first_name} {applicant.last_name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowConvertModal(false)}><X size={18} /></Button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Division *</label>
                  <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm mt-1" value={convertForm.division}
                    onChange={e => setConvertForm(prev => ({ ...prev, division: e.target.value }))} data-testid="convert-division">
                    <option value="Therapy">Therapy</option>
                    <option value="Office">Office</option>
                    <option value="Nursing">Nursing</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Discipline</label>
                  <Input value={convertForm.discipline} onChange={e => setConvertForm(prev => ({ ...prev, discipline: e.target.value }))}
                    placeholder="PT, OT, SLP..." className="mt-1" data-testid="convert-discipline" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Personnel Type</label>
                  <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm mt-1" value={convertForm.personnel_type}
                    onChange={e => setConvertForm(prev => ({ ...prev, personnel_type: e.target.value }))} data-testid="convert-type">
                    <option value="Employee">Employee</option>
                    <option value="Contractor">Contractor</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Job Title</label>
                  <Input value={convertForm.job_title} onChange={e => setConvertForm(prev => ({ ...prev, job_title: e.target.value }))}
                    placeholder="Job title" className="mt-1" data-testid="convert-title" />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Hire Date *</label>
                <Input type="date" value={convertForm.hire_date} onChange={e => setConvertForm(prev => ({ ...prev, hire_date: e.target.value }))}
                  className="mt-1" data-testid="convert-hire-date" />
              </div>

              <div>
                <label className="text-sm font-medium">Onboarding</label>
                <select className="flex h-10 w-full rounded-md border px-3 py-2 text-sm mt-1" value={convertForm.onboarding_pack_id}
                  onChange={e => setConvertForm(prev => ({ ...prev, onboarding_pack_id: e.target.value }))} data-testid="convert-onboarding">
                  <option value="">Assign onboarding later</option>
                  {onboardingPacks.map(p => <option key={p.id} value={p.id}>Use preset: {p.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Pick a preset now, or choose "later" and build it from the personnel Onboarding tab.
                </p>
              </div>

              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <p className="font-medium mb-1">What happens:</p>
                <ul className="list-disc list-inside text-muted-foreground space-y-0.5 text-xs">
                  <li>New personnel record created with applicant's info</li>
                  <li>Personnel status set to "Onboarding"</li>
                  <li>Applicant status set to "Hired"</li>
                  <li>Portal account created + activation link (emailed if email is connected, else copyable on the record)</li>
                  {convertForm.onboarding_pack_id
                    ? <li>Onboarding tasks assigned; signature documents (e.g. 1099 contract, contractor paperwork) auto-sent for signing. Status auto-flips to "Active" when all required items are done</li>
                    : <li>No pack selected — stays "Onboarding" until staff click "Mark Active"</li>}
                </ul>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowConvertModal(false)}>Cancel</Button>
                <Button onClick={handleConvert} disabled={converting} className="bg-primary gap-1.5" data-testid="confirm-convert-btn">
                  <UserPlus size={16} />
                  {converting ? 'Converting...' : 'Convert & Create Personnel'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}


      {/* Hire Modal */}
      {showHireModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Send Offer Letter</h2>
                <p className="text-sm text-muted-foreground">
                  To: {applicant.first_name} {applicant.last_name}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowHireModal(false)}>
                <X size={18} />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Template Selection */}
              <div>
                <label className="text-sm font-medium">Select Offer Letter Template *</label>
                {offerTemplates.length === 0 ? (
                  <div className="mt-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      No offer letter templates found. Please create one in{' '}
                      <span
                        className="underline cursor-pointer"
                        onClick={() => navigate('/admin/signatures')}
                      >
                        Signature Documents
                      </span>{' '}
                      first.
                    </p>
                  </div>
                ) : (
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = offerTemplates.find((t) => t.id === e.target.value);
                      handleSelectTemplate(template);
                    }}
                  >
                    <option value="">Select a template...</option>
                    {offerTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-medium">Recipient Email</label>
                <Input
                  value={applicant.email || ''}
                  disabled
                  className="mt-1 bg-muted"
                />
                {!applicant.email && (
                  <p className="text-xs text-red-500 mt-1">
                    This applicant doesn't have an email address. Please add one first.
                  </p>
                )}
              </div>

              {/* Pre-fill field values (optional) */}
              {(() => {
                const prefillFields = (selectedTemplate?.fields || []).filter((f) =>
                  ['text', 'date', 'name', 'email'].includes(f.type)
                );
                if (!prefillFields.length) return null;
                return (
                  <div className="space-y-3" data-testid="offer-prefill-section">
                    <div>
                      <label className="text-sm font-medium">Pre-fill Offer Details (optional)</label>
                      <p className="text-xs text-muted-foreground">
                        These are filled in before the applicant signs. They can still be edited at signing time.
                      </p>
                    </div>
                    {prefillFields.map((f) => (
                      <div key={f.id}>
                        <label className="text-xs text-muted-foreground capitalize">
                          {f.label || f.type}
                        </label>
                        <Input
                          type={f.type === 'date' ? 'date' : 'text'}
                          value={placeholderValues[f.id] || ''}
                          onChange={(e) =>
                            setPlaceholderValues({
                              ...placeholderValues,
                              [f.id]: e.target.value,
                            })
                          }
                          placeholder={f.placeholder || `Enter ${f.label || f.type}`}
                          data-testid={`offer-prefill-${f.id}`}
                        />
                      </div>
                    ))}
                  </div>
                );
              })()}

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowHireModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSendOffer}
                  disabled={!selectedTemplate || !applicant.email || sendingOffer}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="send-offer-btn"
                >
                  <Send size={16} className="mr-2" />
                  {sendingOffer ? 'Sending...' : 'Send Offer Letter'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <ScheduleEventModal
        open={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        relatedType="applicant"
        relatedId={applicantId}
        defaultAttendee={applicant ? {
          kind: 'applicant',
          ref_id: applicant.id,
          name: `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim(),
          email: applicant.email || null,
        } : null}
        onSaved={() => setShowScheduleModal(false)}
      />

      <SelfScheduleLinkModal
        open={showSelfScheduleModal}
        onClose={() => setShowSelfScheduleModal(false)}
        applicant={applicant}
      />
    </div>
  );
}
